import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import { CheckIcon, ClockIcon, MailIcon } from '../utils/IconWrapper';
import { useAuth } from '../utils/AuthContext';
import { supabase } from '../utils/supabaseClient';

const ConfirmationContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  text-align: center;
`;

const StatusIcon = styled.div<{ status: 'success' | 'pending' | 'error' }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  font-size: 2rem;
  
  ${props => {
    switch (props.status) {
      case 'success':
        return 'background: #22c55e; color: white;';
      case 'pending':
        return 'background: #f59e0b; color: white;';
      case 'error':
        return 'background: #ef4444; color: white;';
      default:
        return 'background: #6b7280; color: white;';
    }
  }}
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #333;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: #6b7280;
  margin-bottom: 2rem;
`;

const BookingDetails = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 2rem;
  margin: 2rem 0;
  text-align: left;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e1e5e9;
  
  &:last-child {
    border-bottom: none;
    font-weight: 600;
    font-size: 1.1rem;
  }
`;

const DetailLabel = styled.span`
  color: #6b7280;
`;

const DetailValue = styled.span`
  color: #333;
  font-weight: 500;
`;

const InfoBox = styled.div`
  background: #e0f2fe;
  border: 1px solid #0891b2;
  border-radius: 8px;
  padding: 1.5rem;
  margin: 2rem 0;
  text-align: left;
`;

const InfoTitle = styled.h3`
  color: #0891b2;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InfoList = styled.ul`
  margin: 0;
  padding-left: 1.5rem;
  color: #333;
  
  li {
    margin-bottom: 0.5rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

interface BookingConfirmationProps {}

const BookingConfirmation: React.FC<BookingConfirmationProps> = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [booking, setBooking] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<'success' | 'pending' | 'error'>('pending');

  const paymentIntentId = searchParams.get('payment_intent');
  const bookingId = searchParams.get('booking_id');

  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (!bookingId) {
        setStatus('error');
        setLoading(false);
        return;
      }

      try {
        const { data: bookingData, error } = await supabase
          .from('bookings')
          .select(`
            *,
            accommodations (
              title_it,
              title_en,
              location_it,
              location_en,
              image
            )
          `)
          .eq('id', bookingId)
          .single();

        if (error) throw error;

        setBooking(bookingData);
        
        // Determine status based on booking data
        if (bookingData.payment_status === 'paid') {
          setStatus('success');
        } else if (bookingData.payment_status === 'pending') {
          setStatus('pending');
        } else {
          setStatus('error');
        }

      } catch (error) {
        console.error('Error fetching booking:', error);
        setStatus('error');
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [bookingId]);

  const getStatusContent = () => {
    switch (status) {
      case 'success':
        return {
          icon: <CheckIcon />,
          title: 'Prenotazione Confermata!',
          subtitle: 'La tua prenotazione è stata elaborata con successo.',
        };
      case 'pending':
        return {
          icon: <ClockIcon />,
          title: 'Prenotazione in Elaborazione',
          subtitle: 'Stiamo elaborando il tuo pagamento. Ti invieremo una conferma a breve.',
        };
      case 'error':
        return {
          icon: '❌',
          title: 'Errore nella Prenotazione',
          subtitle: 'Si è verificato un problema. Ti preghiamo di contattarci.',
        };
    }
  };

  const statusContent = getStatusContent();

  if (loading) {
    return (
      <Layout>
        <ConfirmationContainer>
          <div>Caricamento dettagli prenotazione...</div>
        </ConfirmationContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <ConfirmationContainer>
        <StatusIcon status={status}>
          {statusContent.icon}
        </StatusIcon>
        
        <Title>{statusContent.title}</Title>
        <Subtitle>{statusContent.subtitle}</Subtitle>

        {booking && (
          <BookingDetails>
            <h3 style={{ marginBottom: '1rem', color: '#333' }}>Dettagli Prenotazione</h3>
            
            <DetailRow>
              <DetailLabel>ID Prenotazione:</DetailLabel>
              <DetailValue>#{booking.id.slice(0, 8)}</DetailValue>
            </DetailRow>
            
            <DetailRow>
              <DetailLabel>Alloggio:</DetailLabel>
              <DetailValue>{booking.accommodations?.title_it || 'N/A'}</DetailValue>
            </DetailRow>
            
            <DetailRow>
              <DetailLabel>Località:</DetailLabel>
              <DetailValue>{booking.accommodations?.location_it || 'N/A'}</DetailValue>
            </DetailRow>
            
            <DetailRow>
              <DetailLabel>Check-in:</DetailLabel>
              <DetailValue>{new Date(booking.check_in).toLocaleDateString('it-IT')}</DetailValue>
            </DetailRow>
            
            <DetailRow>
              <DetailLabel>Check-out:</DetailLabel>
              <DetailValue>{new Date(booking.check_out).toLocaleDateString('it-IT')}</DetailValue>
            </DetailRow>
            
            <DetailRow>
              <DetailLabel>Ospiti:</DetailLabel>
              <DetailValue>{booking.guests_count}</DetailValue>
            </DetailRow>
            
            <DetailRow>
              <DetailLabel>Nome Ospite:</DetailLabel>
              <DetailValue>{booking.guest_name}</DetailValue>
            </DetailRow>
            
            <DetailRow>
              <DetailLabel>Email:</DetailLabel>
              <DetailValue>{booking.guest_email}</DetailValue>
            </DetailRow>
            
            <DetailRow>
              <DetailLabel>Totale Pagato:</DetailLabel>
              <DetailValue>€{booking.total_price.toFixed(2)}</DetailValue>
            </DetailRow>
          </BookingDetails>
        )}

        {status === 'success' && (
          <InfoBox>
            <InfoTitle>
              <MailIcon />
              Prossimi Passi
            </InfoTitle>
            <InfoList>
              <li>Riceverai un'email di conferma all'indirizzo fornito</li>
              <li>Ti invieremo le istruzioni per il check-in 24-48 ore prima dell'arrivo</li>
              <li>Controlla la tua email per ulteriori dettagli</li>
              <li>Per domande, contattaci a <strong>info@dhevylgroup.com</strong></li>
            </InfoList>
          </InfoBox>
        )}

        {status === 'pending' && (
          <InfoBox>
            <InfoTitle>
              <ClockIcon />
              Elaborazione in Corso
            </InfoTitle>
            <InfoList>
              <li>Il tuo pagamento è in fase di elaborazione</li>
              <li>Riceverai una conferma entro 10 minuti</li>
              <li>Controlla la tua email per aggiornamenti</li>
              <li>Se non ricevi conferma, contattaci</li>
            </InfoList>
          </InfoBox>
        )}

        {status === 'error' && (
          <InfoBox style={{ background: '#fef2f2', borderColor: '#ef4444' }}>
            <InfoTitle style={{ color: '#ef4444' }}>
              ❌ Richiesta Assistenza
            </InfoTitle>
            <InfoList>
              <li>Si è verificato un problema con la tua prenotazione</li>
              <li>Il tuo pagamento potrebbe non essere stato elaborato</li>
              <li>Contatta il nostro supporto: <strong>info@dhevylgroup.com</strong></li>
              <li>Includi l'ID prenotazione: <strong>#{bookingId?.slice(0, 8)}</strong></li>
            </InfoList>
          </InfoBox>
        )}

        <ButtonGroup>
          <Button
            onClick={() => navigate('/dashboard')}
            style={{ backgroundColor: '#0d9488' }}
          >
            Vai al Dashboard
          </Button>
          
          <Button
            onClick={() => navigate('/accommodations')}
            variant="outline"
          >
            Esplora Altri Alloggi
          </Button>
          
          <Button
            onClick={() => navigate('/contact')}
            variant="outline"
          >
            Contattaci
          </Button>
        </ButtonGroup>
      </ConfirmationContainer>
    </Layout>
  );
};

export default BookingConfirmation; 